import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, mergeMap, of, switchMap, tap } from 'rxjs';
import { GraphQlService } from '@shared/services/graphql-service';
import { SwalService } from '@shared/services/swal.service';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { CREATE_PRODUCT_USAGE, GET_PRODUCT_USAGE, UPDATE_PRODUCT_USAGE } from '../products.graphql';
import { CreateProductUsageResponse, GetProductUsageResponse, UpdateProductUsageResponse } from '../types/product-usage';
import { ProductUsageActions } from './product-usage.actions';

@Injectable()
export class ProductUsageEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphqlService = inject(GraphQlService);
  private readonly swalService = inject(SwalService);

  loadAll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductUsageActions.loadAll),
      switchMap(() =>
        this.graphqlService
          .query<GetProductUsageResponse>({
            query: GET_PRODUCT_USAGE,
            apolloNamespace: ApolloNamespace.registration,
          })
          .pipe(
            map((response) =>
              ProductUsageActions.loadAllSuccess({
                usages: response.data?.getProductUsage?.data ?? [],
              })
            ),
            catchError((error) => of(ProductUsageActions.loadAllFailure({ error })))
          )
      )
    )
  );

  create$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductUsageActions.create),
      mergeMap(({ usageName, isTransit, isLocal }) =>
        this.graphqlService
          .mutate<CreateProductUsageResponse>({
            mutation: CREATE_PRODUCT_USAGE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { usageName, isTransit, isLocal },
          })
          .pipe(
            map((response) => {
              const res = response.data?.createProductUsage;
              if (res?.status && res.data) {
                return ProductUsageActions.createSuccess({ entity: res.data });
              }

              return ProductUsageActions.createFailure({
                error: res?.message ?? 'Failed to create product usage',
              });
            }),
            catchError((error) => of(ProductUsageActions.createFailure({ error })))
          )
      )
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProductUsageActions.update),
      mergeMap(({ productUsageUuId, usageName, isTransit, isLocal }) =>
        this.graphqlService
          .mutate<UpdateProductUsageResponse>({
            mutation: UPDATE_PRODUCT_USAGE,
            apolloNamespace: ApolloNamespace.registration,
            variables: { productUsageUuId, usageName, isTransit, isLocal },
          })
          .pipe(
            map((response) => {
              const res = response.data?.updateProductUsage;
              if (res?.status && res.data) {
                return ProductUsageActions.updateSuccess({ entity: res.data });
              }

              return ProductUsageActions.updateFailure({
                error: res?.message ?? 'Failed to update product usage',
              });
            }),
            catchError((error) => of(ProductUsageActions.updateFailure({ error })))
          )
      )
    )
  );

  notifications$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          ProductUsageActions.loadAllFailure,
          ProductUsageActions.createSuccess,
          ProductUsageActions.createFailure,
          ProductUsageActions.updateSuccess,
          ProductUsageActions.updateFailure
        ),
        tap((action) => {
          if (action.type === ProductUsageActions.createSuccess.type) {
            this.swalService.successToast('Product usage created successfully');
          } else if (action.type === ProductUsageActions.updateSuccess.type) {
            this.swalService.successToast('Product usage updated successfully');
          } else if (
            action.type === ProductUsageActions.createFailure.type ||
            action.type === ProductUsageActions.updateFailure.type
          ) {
            const anyAction: any = action;
            this.swalService.errorToast(anyAction.error?.toString?.() ?? 'Operation failed');
          } else if (action.type === ProductUsageActions.loadAllFailure.type) {
            this.swalService.errorToast('Failed to load product usages');
          }
        })
      ),
    { dispatch: false }
  );
}
